import React, { useEffect, useState } from "react"
import { Col, Form, Row } from "react-bootstrap"
import { injectIntl } from "react-intl"
import { useDispatch, useSelector } from "react-redux"
import Select from "react-select"
import DocumentUploaderDropZone from "../../common/document-uploader/dropzone.component"
import { SelectOption } from "../../common/types"
import {
    IMPORT_STOCK_FIELD,
    INVENTORY_IMPORT_FILE_URL,
    SUPPLIER_COSTS_IMPORT_FILE_URL,
    StockNewImportFormProps
} from "../../common/types/inventory"
import { GlobalHelper } from "../../indexImports"
import IntlProps from "../../state/helpers/IntlProps"
import { INTEGRATION_KEY } from "../../state/integration/Constants"
import { InventoryLocationAction } from "../../state/inventory/location/actions"
import { InventoryLocationSelector } from "../../state/inventory/location/selectors"
import { EXPORT_TYPE, IMPORT_FROM } from "../../state/product/Types"
import { SUPPLIER_COST } from "../../state/product/models"
import { STOCK_IMPORT_FORM_INTEGRATION_MESSAGES, STOCK_IMPORT_FORM_MESSAGES, STOCK_IMPORT_MODAL_MESSAGES } from "../../utils/inventory"

const StockNewImportForm = (props: StockNewImportFormProps & IntlProps) => {
    const dispatch = useDispatch()
    const state = useSelector(state => InventoryLocationSelector.get(state))
    const [inventoryLocations, setInventoryLocations] = useState<Array<SelectOption<string>>>([])

    useEffect(() => {
        if (state.listing.length === 0) {
            dispatch(InventoryLocationAction.fetchListing())
        }
    }, [])

    useEffect(() => {
        setInventoryLocations(state.listing.map(loc => {
            return {
                value: loc.id,
                label: loc.name
            }
        }))
    }, [state])

    const getImportFromOptions = (): Array <SelectOption<string>> => {
        const options = [
            {
                value: IMPORT_FROM.CSV,
                label: props.intl.formatMessage(STOCK_IMPORT_FORM_MESSAGES["fromCsv"])
            }
        ]
        if (GlobalHelper.activeIntegrationSupportsInventorySync()) {
            options.push({
                value: IMPORT_FROM.INTEGRATION,
                label: props.intl.formatMessage(STOCK_IMPORT_FORM_MESSAGES["fromIntegration"])
            })
        }
        return options
    }

    const getImportTypesOptions = (): Array<SelectOption<string>> => {
        return [
            {
                value: SUPPLIER_COST,
                label: props.intl.formatMessage(STOCK_IMPORT_MODAL_MESSAGES["importTypeSupplierCost"])
            },
            {
                value: EXPORT_TYPE.INVENTORY_LEVELS,
                label: props.intl.formatMessage(STOCK_IMPORT_MODAL_MESSAGES["importTypeInventoryLevel"])
            }
        ]
    }

    //TODO: make it more dynamic when it is actually used.
    const getIntegrationImportOptions = (): Array<SelectOption<string>> => {

        return [
            {
                value: INTEGRATION_KEY.WIX,
                label: props.intl.formatMessage(STOCK_IMPORT_FORM_INTEGRATION_MESSAGES["wix"])
            },
            {
                value: INTEGRATION_KEY.FRESHBOOKS,
                label: props.intl.formatMessage(STOCK_IMPORT_FORM_INTEGRATION_MESSAGES["freshbooks"])
            }
        ]
    }

    const handleFormChange = (field: string, value: string) => {
        props.onFormChangeCallback(field, value)
    }

    const handleDocumentChange = (document) => {
        let fileId = ""
        if (document && document.length > 0) {
            fileId = document[0].id
        }
        props.onFormChangeCallback(IMPORT_STOCK_FIELD.FILE, fileId)
    }

    return (
        <Form className="container-fluid">
            <Form.Group className="mb-3">
                <Row>
                    <Col xs={4}>
                        <Form.Label className="custom-form-label">
                            {props.intl.formatMessage(STOCK_IMPORT_FORM_MESSAGES["importType"])}
                        </Form.Label>
                    </Col>
                    <Col xs={8}>
                        <Select
                            value={props.importType}
                            onChange={(op) => handleFormChange(IMPORT_STOCK_FIELD.IMPORT_TYPE, op?.value)}
                            multi={false}
                            defaultValue={props.intl.formatMessage(STOCK_IMPORT_FORM_MESSAGES["inventoryLocationPlaceholder"])}
                            options={getImportTypesOptions()}
                        />
                        <small className="text-danger">{props.errors.importType}</small>
                    </Col>
                </Row>
            </Form.Group>
            {
                props.importType == EXPORT_TYPE.INVENTORY_LEVELS && <>
                    <Form.Group className="mb-3">
                        <Row>
                            <Col xs={4}>
                                <Form.Label className="custom-form-label">
                                    {props.intl.formatMessage(STOCK_IMPORT_FORM_MESSAGES["importFrom"])}
                                </Form.Label>
                            </Col>
                            <Col xs={8}>
                                <Select
                                    value={props.importFrom}
                                    onChange={(op) => handleFormChange(IMPORT_STOCK_FIELD.IMPORT_FROM, op?.value)}
                                    multi={false}
                                    defaultValue={props.intl.formatMessage(STOCK_IMPORT_FORM_MESSAGES["inventoryLocationPlaceholder"])}
                                    options={getImportFromOptions()}
                                />
                                <small className="text-danger">{props.errors.importFrom}</small>
                            </Col>
                        </Row>
                    </Form.Group>

                    { props.importFrom == IMPORT_FROM.CSV && <Form.Group className="mb-3">
                        <Row>
                            <Col xs={4}>
                                <Form.Label className="custom-form-label">
                                    {props.intl.formatMessage(STOCK_IMPORT_FORM_MESSAGES["stockLocationLabel"])}
                                </Form.Label>
                            </Col>
                            <Col xs={8}>
                                <Select
                                    value={props.inventoryLocation}
                                    onChange={(op) => handleFormChange(IMPORT_STOCK_FIELD.STOCK_LOCATION, op?.value)}
                                    multi={false}
                                    defaultValue={props.intl.formatMessage(STOCK_IMPORT_FORM_MESSAGES["inventoryLocationPlaceholder"])}
                                    options={inventoryLocations}
                                />
                                <small className="text-danger">{props.errors.inventoryLocation}</small>
                            </Col>
                        </Row>
                    </Form.Group> }

                    { props.importFrom == IMPORT_FROM.INTEGRATION && <Form.Group className="mb-3">
                        <Row>
                            <Col xs={4}>
                                <Form.Label className="custom-form-label">
                                    {props.intl.formatMessage(STOCK_IMPORT_FORM_MESSAGES["fromIntegration"])}
                                </Form.Label>
                            </Col>
                            <Col xs={8}>
                                <Select
                                    value={props.integration}
                                    onChange={(op) => handleFormChange(IMPORT_STOCK_FIELD.INTEGRATION, op?.value)}
                                    multi={false}
                                    defaultValue={props.intl.formatMessage(STOCK_IMPORT_FORM_MESSAGES["inventoryLocationPlaceholder"])}
                                    options={getIntegrationImportOptions()}
                                />
                                <small className="text-danger">{props.errors.integration}</small>
                            </Col>
                        </Row>
                    </Form.Group> } 
                </>
            }
            <Form.Group className="mb-3">
                <Row>
                    <Col xs={4}>
                        <Form.Label className="custom-form-label">
                            {props.intl.formatMessage(STOCK_IMPORT_FORM_MESSAGES["notesLabel"])}
                        </Form.Label>
                    </Col>
                    <Col xs={8}>
                        <textarea
                            className="form-control"
                            id="importStockNotes"
                            name="importStockNotes"
                            rows={3}
                            value={props.notes}
                            onChange={e => handleFormChange(IMPORT_STOCK_FIELD.NOTES, e.target.value)}
                            placeholder={props.intl.formatMessage(STOCK_IMPORT_FORM_MESSAGES["notesPlaceholder"])}
                        />
                    </Col>
                </Row>
            </Form.Group>
            { props.importFrom != IMPORT_FROM.INTEGRATION &&
                <Row>
                    <Col>
                        <DocumentUploaderDropZone
                            onUpdateDocumentList={handleDocumentChange}
                            maxFiles={1}
                            fileTypes={[".csv"]}
                        />
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="d-flex">
                                {props.importType === EXPORT_TYPE.INVENTORY_LEVELS &&
                                    <a href={INVENTORY_IMPORT_FILE_URL} target="_blank" rel="noreferrer">
                                        {props.intl.formatMessage(STOCK_IMPORT_FORM_MESSAGES["downloadCsvTemplate"])}
                                    </a>
                                }
                                {props.importType === SUPPLIER_COST &&
                                    <a href={SUPPLIER_COSTS_IMPORT_FILE_URL} target="_blank" rel="noreferrer">
                                        {props.intl.formatMessage(STOCK_IMPORT_FORM_MESSAGES["downloadCsvTemplate"])}
                                    </a>
                                }
                            </div>
                            <div className="d-flex">
                                <small className="text-danger">{props.errors.file}</small>
                            </div>
                        </div>


                    </Col>
                </Row>
            }
        </Form>
    )
}

export default injectIntl(StockNewImportForm)


// WEBPACK FOOTER //
// ./src/inventory/forms/StockNewImportForm.tsx